/**
 * LegendaryLiveFlash — navbar-mounted spectator funnel.
 *
 * Listens to the `/api/ws/runit/live` WebSocket channel already in use by the
 * homepage LiveMatchesWidget. When a new LEGENDARY-rarity match pops, it:
 *   1) Plays a short amber chime via WebAudio (no assets needed)
 *   2) Flashes the navbar border amber for ~6 seconds
 *   3) Drops a clickable "🔥 LEGENDARY LIVE" pill that routes straight to
 *      /runit/watch/:matchId so visitors become spectators in one tap.
 *
 * Zero backend changes. Self-contained. Mute-aware (honors the global RUN IT
 * sound toggle in localStorage).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, X } from "lucide-react";
import { isSoundEnabled } from "../../lib/runitAudio";
import { API } from "../../utils";

// Small 3-note ascending chime. Nothing heavy — decays in ~0.45s.
const playChime = () => {
  if (typeof window === "undefined") return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [660, 880, 1244]; // E5, A5, D#6
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      const t0 = ctx.currentTime + i * 0.09;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.18, t0 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.4);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 900);
  } catch { /* audio blocked — fine */ }
};

export default function LegendaryLiveFlash() {
  const [alert, setAlert] = useState(null); // { matchId, until }
  const wsRef = useRef(null);
  // Dedup by match_id (WS can replay on reconnect)
  const seenRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    let reconnectTimer = null;

    const connect = () => {
      try {
        const base = API.replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/ws/runit/live`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          if (!alive) return;
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type !== "live_match_new" || !msg.match) return;
            const m = msg.match;
            if ((m.rarity || "").toUpperCase() !== "LEGENDARY") return;
            if (seenRef.current.has(m.match_id)) return;

            // Ignore stale notifications: don't fire the flash for matches
            // whose theatre started more than 25s ago. WS reconnects and
            // bfcache restores can replay old "live_match_new" events
            // referencing matches that have already resolved & dropped off
            // the live feed — clicking the resulting pill would land the
            // user on the "Match not found" dead-end. Added 2026-05-15.
            const startedAt = m.theatre_started_at ? Date.parse(m.theatre_started_at) : 0;
            if (startedAt && Date.now() - startedAt > 25_000) return;

            // Persist seen-ids across navigations so the WS replay buffer
            // doesn't re-fire alerts the user already saw this session.
            try {
              const k = "blk-legendary-seen";
              const arr = JSON.parse(sessionStorage.getItem(k) || "[]");
              if (arr.includes(m.match_id)) { seenRef.current.add(m.match_id); return; }
              arr.push(m.match_id);
              sessionStorage.setItem(k, JSON.stringify(arr.slice(-30)));
            } catch { /* private mode — ok */ }

            seenRef.current.add(m.match_id);
            if (isSoundEnabled()) playChime();
            setAlert({
              matchId: m.match_id,
              a: (m.player_a?.username || (m.player_a?.user_id || "").slice(0, 6)),
              b: (m.player_b?.username || (m.player_b?.user_id || "").slice(0, 6)),
              until: Date.now() + 6500,
            });
            setTimeout(() => {
              setAlert((cur) => (cur && cur.matchId === m.match_id ? null : cur));
            }, 6500);
          } catch { /* ignore */ }
        };
        ws.onclose = () => {
          if (!alive) return;
          // Reconnect with gentle backoff (5s)
          reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => { /* onclose will handle reconnect */ };
      } catch {
        if (alive) reconnectTimer = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, []);

  if (!alert) return null;

  return (
    <>
      {/* Full-width amber flash across the navbar border */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] z-[51]"
        style={{ animation: "legendaryNavFlash 6.5s ease-out forwards" }}
        aria-hidden
      />
      {/* Clickable pill — drops from the top of the navbar, auto-dismisses */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-[52] pointer-events-auto"
           style={{ animation: "legendaryPillDrop 0.45s cubic-bezier(.2,.9,.3,1.3) both" }}>
        <div className="flex items-center gap-2 bg-gradient-to-r from-[#1a0a05] via-[#0a0608] to-[#1a0a05] border-2 border-amber-400 rounded-full px-3 py-1.5 shadow-[0_0_40px_-4px_rgba(251,191,36,0.9)]">
          <Flame size={14} className="text-amber-300 animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-black text-amber-200 hidden sm:inline">
            LEGENDARY · Live
          </span>
          <span className="text-[10px] uppercase tracking-[0.3em] font-black text-amber-200 sm:hidden">
            LEG · Live
          </span>
          <Link
            to={`/runit/watch/${alert.matchId}`}
            onClick={() => setAlert(null)}
            className="ml-1 text-white text-[11px] font-black uppercase tracking-wider hover:text-amber-200 whitespace-nowrap"
            data-testid="legendary-live-watch-link"
          >
            @{alert.a} vs @{alert.b} · WATCH →
          </Link>
          <button
            onClick={() => setAlert(null)}
            className="ml-1 text-gray-400 hover:text-white"
            aria-label="Dismiss"
            data-testid="legendary-live-dismiss"
          >
            <X size={12} />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes legendaryNavFlash {
          0%   { background: linear-gradient(90deg, transparent 0%, rgba(251,191,36,0.0) 0%, rgba(251,191,36,0) 100%); box-shadow: 0 0 0 rgba(251,191,36,0); }
          8%   { background: linear-gradient(90deg, rgba(251,191,36,0.0) 0%, rgba(251,191,36,1) 50%, rgba(251,191,36,0.0) 100%); box-shadow: 0 0 28px rgba(251,191,36,0.8); }
          55%  { background: linear-gradient(90deg, rgba(251,191,36,0.0) 0%, rgba(251,191,36,0.7) 50%, rgba(251,191,36,0.0) 100%); box-shadow: 0 0 18px rgba(251,191,36,0.5); }
          100% { background: linear-gradient(90deg, transparent 0%, transparent 100%); box-shadow: 0 0 0 rgba(251,191,36,0); }
        }
        @keyframes legendaryPillDrop {
          0%   { opacity: 0; transform: translate(-50%, -14px) scale(0.85); }
          60%  { opacity: 1; transform: translate(-50%, 4px)   scale(1.04); }
          100% { opacity: 1; transform: translate(-50%, 0)     scale(1); }
        }
      `}</style>
    </>
  );
}
